import React from "react";
import awsconfig from "./aws-exports";
import { Amplify } from "aws-amplify";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { Provider as ProviderStore } from "mobx-react";
import "./index.css";
import DefaultStore from "./store/Store";
// SECURITY: Disable console in production to prevent leaking sensitive information
// import "./utils/devConsole";
import { createRoutes } from "./routes/routesConfig";
import { ensureSessionBeforeRouter } from "./routes/loaders";
import { initUserflow } from "@/utils/userflow";
import { ensurePwaInstallCapture } from "@/utils/pwaInstall";

/**
 * App build version (injected at build time via Vite `define`).
 * Use anywhere in app code: reference the identifier `__APP_VERSION__` (string), e.g.
 * `Typography` children or `title={\`About (\${__APP_VERSION__})\`}`.
 * @type {string}
 */
window.appVersion = __APP_VERSION__;

if (import.meta.env.DEV && typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  // Drop stale SW/cache from prior prod/preview sessions on the same origin (e.g. localhost:3000).
  void navigator.serviceWorker.getRegistrations().then((registrations) =>
    Promise.all(registrations.map((registration) => registration.unregister()))
  );
  if (typeof caches !== "undefined") {
    void caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
  }
} else if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  // When the CDN does not cache sw.js (see amplify customHeaders), nudge the browser to
  // fetch an updated service worker after deploy — especially for long-lived tabs.
  const pingServiceWorkerUpdate = () => {
    navigator.serviceWorker.getRegistration().then((registration) => {
      registration?.update();
    });
  };
  window.addEventListener("load", () => {
    setTimeout(pingServiceWorkerUpdate, 0);
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      pingServiceWorkerUpdate();
    }
  });
}

// SECURITY: Single bootstrap — passwordless OTP uses CUSTOM_WITHOUT_SRP at signIn call sites (see amplifyAuth.js).
Amplify.configure(awsconfig);

// Initialize Userflow once per page load (browser-only; no-op without VITE_USERFLOW_TOKEN).
initUserflow();

// Capture Chromium PWA install prompt on first load (before Settings mounts).
ensurePwaInstallCapture();

/**
 * Routes are defined in routes/routesConfig.jsx with createBrowserRouter.
 * Option A: hydrate AuthStore (same path as route loaders) before the router exists so loaders are not racing MobX.
 *
 * See: https://reactrouter.com/en/main/routers/create-browser-router
 */
async function bootstrapApp() {
  await ensureSessionBeforeRouter();
  const router = createBrowserRouter(createRoutes());
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <ProviderStore myStore={DefaultStore}>
        <RouterProvider router={router} />
      </ProviderStore>
    </React.StrictMode>
  );
}

bootstrapApp();
